import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Grid_ColumnsRows() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🟦 CSS Grid: Columns & Rows</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS Grid allows you to define columns and rows in multiple ways, using
        fixed, flexible, and dynamic sizing. Understanding all possibilities
        helps you create robust and responsive layouts.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>fr</code>: fraction of remaining space.
          </li>
          <li>
            <code>px</code> or <code>%</code>: fixed or relative sizes.
          </li>
          <li>
            <code>auto</code>: size based on content.
          </li>
          <li>
            <code>minmax(min, max)</code>: flexible range.
          </li>
          <li>
            <code>repeat(n, size)</code>: repeat column/row pattern.
          </li>
          <li>
            <code>auto-fit</code> / <code>auto-fill</code>: responsive repeat
            for available space.
          </li>
        </ul>
      </div>

      {/* Example 1: Fixed Columns */}
      <h2 className="section-title">1️⃣ Fixed Columns (px)</h2>
      <CodeCopyBlock
        code={`.grid-fixed {
  display: grid;
  grid-template-columns: 100px 150px 100px;
  grid-template-rows: 50px 100px;
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 150px 100px",
          gridTemplateRows: "50px 100px",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#3b82f6",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 2: Fractional Columns */}
      <h2 className="section-title">2️⃣ Fractional Columns (fr)</h2>
      <CodeCopyBlock
        code={`.grid-fr {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  grid-template-rows: 1fr 2fr;
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr 1fr",
          gridTemplateRows: "1fr 2fr",
          gap: "10px",
          height: "200px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#10b981",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 3: Auto Columns & Rows */}
      <h2 className="section-title">3️⃣ Auto Columns & Rows</h2>
      <CodeCopyBlock
        code={`.grid-auto {
  display: grid;
  grid-template-columns: auto auto auto;
  grid-template-rows: auto auto;
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto auto",
          gridTemplateRows: "auto auto",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            background: "#f97316",
            color: "white",
            padding: "10px",
            borderRadius: "8px",
          }}
        >
          Short
        </div>
        <div
          style={{
            background: "#ea580c",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Medium length
        </div>
        <div
          style={{
            background: "#fbbf24",
            color: "white",
            padding: "50px",
            borderRadius: "8px",
          }}
        >
          Long content box
        </div>
        <div
          style={{
            background: "#f97316",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          Box 4
        </div>
        <div
          style={{
            background: "#ea580c",
            color: "white",
            padding: "15px",
            borderRadius: "8px",
          }}
        >
          Box 5
        </div>
        <div
          style={{
            background: "#fbbf24",
            color: "white",
            padding: "25px",
            borderRadius: "8px",
          }}
        >
          Box 6
        </div>
      </div>

      {/* Example 4: minmax() */}
      <h2 className="section-title">4️⃣ minmax()</h2>
      <CodeCopyBlock
        code={`.grid-minmax {
  display: grid;
  grid-template-columns: minmax(100px, 1fr) minmax(150px, 2fr) minmax(100px, 1fr);
  grid-template-rows: minmax(50px, auto) minmax(100px, auto);
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(100px,1fr) minmax(150px,2fr) minmax(100px,1fr)",
          gridTemplateRows: "minmax(50px,auto) minmax(100px,auto)",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 5: repeat() */}
      <h2 className="section-title">5️⃣ repeat()</h2>
      <CodeCopyBlock
        code={`.grid-repeat {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 100px);
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gridTemplateRows: "repeat(2,100px)",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#10b981",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Example 6: auto-fit / auto-fill */}
      <h2 className="section-title">6️⃣ auto-fit / auto-fill</h2>
      <CodeCopyBlock
        code={`.grid-auto-fit {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}`}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#f97316",
              color: "white",
              padding: "30px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine different sizing units in <code>grid-template-columns</code>{" "}
            and <code>grid-template-rows</code>.
          </li>
          <li>
            Use <code>minmax()</code> with <code>auto-fit</code> for fully
            responsive grids.
          </li>
          <li>
            Experiment with implicit rows and columns by adding more items than
            defined.
          </li>
          <li>
            Create responsive dashboards or card layouts using these techniques.
          </li>
          <li>
            Mix with <code>grid-area</code> for advanced positioning.
          </li>
        </ul>
      </div>
    </div>
  );
}
